import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppConstant } from '../../../../Assests/AppConstant';
import { ReactComponent as DownloadIconLight } from '../../../../Assests/Svg/download-icon-light.svg';
import { ReactComponent as DownloadIconDark } from '../../../../Assests/Svg/download-icon-dark.svg';
import { sagaActions } from '../../../../../sagas/sagaActions';
import { getDefaultDashboardSelector } from '../../../../../redux/reducers/reducerSlices/DefaultDashboardReducer';
import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import './DownloadSection.css';

const DownloadSection = (props) => {
    const dispatch = useDispatch();
    const { themeSelected, currentTableRef, name, singlePageDownload, columnsToRemove, extraDataToShow } = props;
    const dashboard = useSelector(getDefaultDashboardSelector);

    const downloadData = (formatStr) => {
        if (formatStr === 'pdf') {
            generatePdf()
        } else if (formatStr === 'excel') {
            generateExcel();
        }
    }

    /**
 * Prepares a table for export (Excel or PDF)
 * - Removes specified columns
 * - Adds extra data (report name + filters) above the table
 * - Wraps everything in a container to ensure PDF rendering
 *
 * @param {HTMLElement|string} tableInput - DOM element (Excel) or HTML string (PDF)
 * @param {number[]} columnsToRemove - Column indexes to remove (zero-based)
 * @param {Object} [extraDataToShow] - Optional extra report data to display above the table
 * @returns {HTMLElement|string} - Modified table element (Excel) or HTML string (PDF)
 */
const prepareTableForExport = (tableInput, columnsToRemove = [], extraDataToShow = null) => {
  if (!tableInput) return tableInput;

  const sortedIndexes = [...columnsToRemove].sort((a, b) => b - a); // remove from last to first
  let tableElement;
  let tableElementWrapper = document.createElement("div"); // wrapper for extraData + table
  let isHTMLString = false;

  // --- Detect input type ---
  if (typeof tableInput === "string") {
    isHTMLString = true;
    const parser = new DOMParser();
    const doc = parser.parseFromString(tableInput, "text/html");
    tableElement = doc.querySelector("table");
  } else {
    tableElement = tableInput.cloneNode(true); // clone to avoid mutating the original table
  }

  if (!tableElement) return tableInput;

  // --- STEP 1: Remove specified columns ---
  const removeColumns = (rows) => {
    rows.forEach((tr) => {
      sortedIndexes.forEach((index) => {
        const cell = tr.children[index];
        if (cell) cell.remove();
      });
    });
  };

  removeColumns(tableElement.querySelectorAll("thead tr"));
  removeColumns(tableElement.querySelectorAll("tbody tr"));

  // --- STEP 2: Add extra report data above the table ---
  if (extraDataToShow) {
    const infoDiv = document.createElement("div");
    infoDiv.style.marginBottom = "12px";
    infoDiv.style.fontFamily = "Arial, sans-serif";
    infoDiv.style.border = "1px solid #ccc";
    infoDiv.style.padding = "10px";
    infoDiv.style.borderRadius = "6px";
    infoDiv.style.background = "#f9f9f9";

    // Report title
    let html = `<div style="font-size:16px;font-weight:bold;margin-bottom:8px;">${extraDataToShow.reportName || "Report"}</div>`;

    // Filters / headings
    if (extraDataToShow.filter) {
      html += `<div style="margin-bottom:8px;">`; // wrapper for filters

      Object.values(extraDataToShow.filter).forEach(obj => {
        // Nested object (like dateRange)
        if (obj && typeof obj === "object" && Object.values(obj).some(f => f.show !== undefined)) {
          Object.values(obj).forEach(field => {
            if (field.show) {
              html += `<div style="font-size:13px;"><strong>${field.name}:</strong> ${field.value || '-'}</div>`;
            }
          });
        } else if (obj && obj.show !== undefined) {
          // Single field object (like currentFilterSelected)
          if (obj.show) {
            html += `<div style="font-size:13px;"><strong>${obj.name}:</strong> ${obj.value || '-'}</div>`;
          }
        }
      });

      html += `</div>`;
    }

    infoDiv.innerHTML = html;
    tableElementWrapper.appendChild(infoDiv);
  }

  // --- STEP 3: Append the table ---
  tableElementWrapper.appendChild(tableElement);

  // --- STEP 4: Return based on input type ---
  return isHTMLString
    ? tableElementWrapper.outerHTML
    : tableElementWrapper;
};

    const generatePdf = () => {

        const inputElement = currentTableRef.innerHTML
        let cleanedTableHTML = null;
        const hasColumnsToRemove = columnsToRemove && columnsToRemove.length > 0;
        const hasExtraData = extraDataToShow && Object.keys(extraDataToShow).length > 0;

        if (hasColumnsToRemove || hasExtraData) {
            cleanedTableHTML = prepareTableForExport(inputElement, columnsToRemove, extraDataToShow);
        } else {
            cleanedTableHTML = inputElement; // use as-is
        }
        console.log("🚀 ~ generatePdf ~ cleanedTableHTML:", cleanedTableHTML)
        const options = {
            margin: 0.1,
            filename: name ? name + '.pdf' : 'drilldown.pdf',
            image: { type: 'jpeg', quality: 1.0 },
            html2canvas: singlePageDownload ? { scale: 3 } : { scale: 3, width: parseInt(window.innerWidth * 2) },
            jsPDF: { unit: 'in', format: 'tabloid', orientation: 'landscape' },
        };

        html2pdf().from(cleanedTableHTML).set(options).toPdf().get('pdf').then(function (pdf) {
            var totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(10);
                pdf.setTextColor(100);
                pdf.text('Page ' + i + ' of ' + totalPages, (pdf.internal.pageSize.getWidth() / 2.3), (pdf.internal.pageSize.getHeight() - 0.8));
            }
        }).save();

    }

    const generateExcel = () => {
        const element = currentTableRef;
        let cleanedTableHTML = null;
       const hasColumnsToRemove = columnsToRemove && columnsToRemove.length > 0;
        const hasExtraData = extraDataToShow && Object.keys(extraDataToShow).length > 0;

        if (hasColumnsToRemove || hasExtraData) {
            cleanedTableHTML = prepareTableForExport(element, columnsToRemove, extraDataToShow);
        } else {
            cleanedTableHTML = element; // use as-is
        }
        const workbook = XLSX.utils.table_to_book(cleanedTableHTML);
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' });
        const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
        FileSaver.saveAs(blob, name ? name + '.xlsx' : 'drilldown.xlsx');
    };

    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; i++) {
            view[i] = s.charCodeAt(i) & 0xff;
        }
        return buf;
    };

    return (
        <div className='dropdown border border-dark rounded-2 p-1 px-2'>

            <div className='d-flex justify-content-start align-items-center me-2 downloadSection' data-bs-toggle="dropdown" aria-expanded="false" >
                {themeSelected === 'light-mode' ? <DownloadIconLight className='me-2' /> : <DownloadIconDark className='me-2' />}
                <div className={`downloadButton ${themeSelected}`}>{AppConstant.downloadSection.downloadTitleStr}</div>
            </div>
            <ul className="dropdown-menu text-center dropDownDownItemloadSection">
                <li><a className="dropdown-item" onClick={() => downloadData('pdf')} href="#">{AppConstant.downloadSection.pdfDownloadString}</a></li>
                <div className='seperator' />
                <li><a className="dropdown-item" onClick={() => downloadData('excel')} href="#">{AppConstant.downloadSection.excelDownloadString}</a></li>
            </ul>
        </div>

    )
}

export default DownloadSection;